import { useEffect, useRef, useState } from "react";
import { ReelItem } from "./ReelItem";
import { ShareMenu } from "./ShareMenu";
import { CommentsDrawer } from "./CommentsDrawer";
import { ManageVideoDialog } from "./ManageVideoDialog";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const ReelsFeed = ({ videos: initial, initialIndex = 0, heightClass = "h-[calc(100vh-6.5rem)]", emptyText = "No videos here yet." }) => {
  const { user } = useAuth();
  const [list, setList] = useState(initial || []);
  const [active, setActive] = useState(initialIndex);
  const [muted, setMuted] = useState(true);
  const [shareVideo, setShareVideo] = useState(null);
  const [commentsVideo, setCommentsVideo] = useState(null);
  const [manageVideo, setManageVideo] = useState(null);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const played = useRef(new Set());

  useEffect(() => { setList(initial || []); }, [initial]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || list.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = Number(entry.target.dataset.index);
            setActive(idx);
          }
        });
      },
      { root, threshold: [0.6] }
    );
    itemRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [list]);

  useEffect(() => {
    // scroll to the requested starting reel once
    const el = itemRefs.current[initialIndex];
    if (el) el.scrollIntoView();
    // eslint-disable-next-line
  }, [initialIndex, list.length]);

  const updateVideo = (id, patch) => {
    setList((l) => l.map((v) => (v.id === id ? { ...v, ...patch } : v)));
    setShareVideo((s) => (s && s.id === id ? { ...s, ...patch } : s));
    setCommentsVideo((c) => (c && c.id === id ? { ...c, ...patch } : c));
  };

  const onPlayed = (id) => {
    if (played.current.has(id)) return;
    played.current.add(id);
    api.post(`/videos/${id}/play`).then((r) => updateVideo(id, { streams: r.data.streams })).catch(() => {});
  };

  const openShare = async (video) => {
    try {
      const { data } = await api.post(`/videos/${video.id}/share`);
      updateVideo(video.id, { shares: data.shares });
      if (data.free) {
        toast.success("Free share unlocked");
      } else if (data.payment_required) {
        toast.message("Share recorded", { description: "The $1 fee + tax will be charged once PayPal is connected." });
      } else {
        toast.success("Share fee charged");
      }
      setShareVideo(video);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not share");
    }
  };

  if (!list || list.length === 0) {
    return <div className={`${heightClass} flex items-center justify-center text-zinc-500`} data-testid="feed-empty">{emptyText}</div>;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={`${heightClass} overflow-y-scroll snap-y snap-mandatory no-scrollbar bg-black`}
        data-testid="reels-feed"
      >
        {list.map((v, i) => (
          <div
            key={v.id}
            ref={(el) => (itemRefs.current[i] = el)}
            data-index={i}
            className="h-full w-full snap-start border-b-4 border-[#FFD60A]"
          >
            <ReelItem
              video={v}
              isActive={active === i}
              shouldLoad={Math.abs(i - active) <= 1}
              muted={muted}
              onToggleMute={() => setMuted((m) => !m)}
              onOpenComments={setCommentsVideo}
              onOpenShare={openShare}
              onShareIntent={openShare}
              onOpenManage={setManageVideo}
              onUpdate={updateVideo}
              onPlayed={onPlayed}
            />
          </div>
        ))}
      </div>

      <ShareMenu open={!!shareVideo} onOpenChange={(o) => !o && setShareVideo(null)} video={shareVideo} />
      <CommentsDrawer open={!!commentsVideo} onClose={() => setCommentsVideo(null)} video={commentsVideo} onCountChange={(id, n) => updateVideo(id, { comment_count: n })} />
      <ManageVideoDialog
        open={!!manageVideo}
        onClose={() => setManageVideo(null)}
        video={manageVideo}
        onSaved={(updated) => updateVideo(updated.id, updated)}
        onDeleted={(id) => setList((l) => l.filter((x) => x.id !== id))}
      />
    </>
  );
};
